import type { AxiosInstance } from 'axios'

import type {
  NegativeKeyword,
  NegativeKeywordBulkResult,
  NegativeKeywordCategory,
} from '../models/keyword.types'
import { decodeBulkResult, decodeKeywordRows } from '../service/keywordDto'

import { DEFINITIONS_REQUEST_CONFIG } from './config'

import { ensureCatalogEntry, type CatalogDeps } from '@/shared/catalog'
import { ApiError, appHttp, decodeDto, unwrap, unwrapEnvelope } from '@/shared/http'

/** Manifest entry name = URL segment = cache key. Both sources carry the same `{data}` body. */
const CATEGORY_CATALOG = 'negative_keyword_categories'

interface NegativeKeywordCategoryBody {
  data: NegativeKeywordCategory[]
}

function isCategoryCatalog(body: unknown): body is NegativeKeywordCategoryBody {
  return (
    typeof body === 'object' && body !== null && Array.isArray((body as { data?: unknown }).data)
  )
}

/** The 409 create/rename answer when the workspace already holds the keyword (any case). */
export function isDuplicateKeywordError(cause: unknown): boolean {
  return ApiError.isApiError(cause) && cause.code === 'DUPLICATE_NEGATIVE_KEYWORD'
}

export class NegativeKeywordApi {
  constructor(
    private readonly http: AxiosInstance = appHttp,
    private readonly catalog: CatalogDeps = {},
  ) {}

  /** Global, workspace-independent metadata — read from the versioned `/catalog/` mirror and
   *  cached across sessions; the legacy endpoint stays as the cold-miss source. */
  async listCategories(): Promise<NegativeKeywordCategory[]> {
    const outcome = await ensureCatalogEntry(CATEGORY_CATALOG, isCategoryCatalog, this.catalog)
    if (outcome.status === 'ready') return outcome.body.data
    return (
      unwrap(
        await this.http.get<NegativeKeywordCategoryBody>(
          '/negative-keyword-categories/',
          DEFINITIONS_REQUEST_CONFIG,
        ),
      )?.data ?? []
    )
  }

  private collection(workspaceId: number | string): string {
    return `/workspaces/${workspaceId}/negative-keywords/`
  }

  /** The list is enveloped (`{data: [...], total}`). Reading the body as the array handed the
   *  table an object, which rendered as the empty state while every keyword was still there. */
  async list(workspaceId: number | string): Promise<NegativeKeyword[]> {
    return decodeDto(
      unwrapEnvelope<unknown>(
        await this.http.get(this.collection(workspaceId), DEFINITIONS_REQUEST_CONFIG),
      ),
      decodeKeywordRows,
    )
  }

  async create(
    workspaceId: number | string,
    keyword: Omit<NegativeKeyword, 'id'>,
  ): Promise<NegativeKeyword> {
    return unwrapEnvelope(
      await this.http.post(this.collection(workspaceId), keyword, DEFINITIONS_REQUEST_CONFIG),
    )
  }

  async update(
    workspaceId: number | string,
    id: number | string,
    keyword: Partial<NegativeKeyword>,
  ): Promise<NegativeKeyword> {
    return unwrapEnvelope(
      await this.http.patch(
        `${this.collection(workspaceId)}${id}/`,
        keyword,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async remove(workspaceId: number | string, id: number | string): Promise<unknown> {
    return unwrapEnvelope(
      await this.http.delete(`${this.collection(workspaceId)}${id}/`, DEFINITIONS_REQUEST_CONFIG),
    )
  }

  async bulkImport(
    workspaceId: number | string,
    items: readonly Omit<NegativeKeyword, 'id'>[],
    skipDuplicates: boolean,
  ): Promise<NegativeKeywordBulkResult> {
    return decodeDto(
      unwrapEnvelope<unknown>(
        await this.http.post(
          `${this.collection(workspaceId)}bulk-import/`,
          { items, skip_duplicates: skipDuplicates },
          DEFINITIONS_REQUEST_CONFIG,
        ),
      ),
      decodeBulkResult,
    )
  }
}

export const negativeKeywordApi = new NegativeKeywordApi()
